import { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { PublicHeader } from "@/components/marketing/PublicHeader";
import { PublicFooter } from "@/components/marketing/PublicFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Mail, Send, Loader2, Calendar, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/lib/edgeFunctionClient";
import { featureFlags, FEATURE_FLAGS } from "@/lib/featureFlags";

const TURNSTILE_SITE_KEY = "0x4AAAAAACHUY2vQKZG8eaL1";

const subjectOptions = [
  { value: "general", label: "General Inquiry" },
  { value: "sales", label: "Sales / Demo Request" },
  { value: "vendor", label: "Vendor Partnership" },
  { value: "support", label: "Support" },
  { value: "feedback", label: "Feedback" },
  { value: "other", label: "Other" },
];

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: any) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    fcWidget?: {
      open: () => void;
    };
  }
}

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const [showCalendarBooking, setShowCalendarBooking] = useState(true);
  const [showLiveChat, setShowLiveChat] = useState(true);
  const [featuresLoading, setFeaturesLoading] = useState(true);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    subject: "",
    message: "",
  });
  
  // Check feature flags for UI visibility
  useEffect(() => {
    const checkFeatureFlags = async () => {
      const [calendarEnabled, chatEnabled] = await Promise.all([
        featureFlags.isEnabled(FEATURE_FLAGS.SHOW_CALENDAR_BOOKING),
        featureFlags.isEnabled(FEATURE_FLAGS.SHOW_LIVE_CHAT),
      ]);
      setShowCalendarBooking(calendarEnabled);
      setShowLiveChat(chatEnabled);
      setFeaturesLoading(false);
    };
    checkFeatureFlags();
  }, []);

  // Load Turnstile script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = () => setTurnstileLoaded(true);
    document.head.appendChild(script);

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
      document.head.removeChild(script);
    };
  }, []);

  // Render Turnstile widget when script is loaded
  useEffect(() => {
    if (turnstileLoaded && turnstileRef.current && window.turnstile && !widgetIdRef.current) {
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => {
          setTurnstileToken(token);
        },
        "expired-callback": () => {
          setTurnstileToken(null);
        },
        "error-callback": () => {
          setTurnstileToken(null);
        },
      });
    }
  }, [turnstileLoaded]);

  const resetTurnstile = () => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      setTurnstileToken(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!turnstileToken) {
      toast({
        title: "Verification Required",
        description: "Please complete the security verification.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.name.trim() || !formData.email.trim() || !formData.subject || !formData.message.trim()) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await invokeEdgeFunction("contact-form-submit", {
        body: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          company: formData.company.trim() || undefined,
          subject: subjectOptions.find(o => o.value === formData.subject)?.label || formData.subject,
          message: formData.message.trim(),
          turnstileToken,
        },
        callerOperation: 'ContactPage.submit',
      });

      if (error) {
        throw new Error(error.message || "Failed to send message");
      }

      const result = data as { error?: string };
      if (result?.error) {
        throw new Error(result.error);
      }

      toast({
        title: "Message Sent!",
        description: "Thank you for reaching out. We'll get back to you within 1-2 business days.",
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        company: "",
        subject: "",
        message: "",
      });
      resetTurnstile();
    } catch (error: any) {
      console.error("Contact form error:", error);
      toast({
        title: "Failed to Send",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
      resetTurnstile();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Radius - B2B Procurement Platform</title>
        <meta
          name="description"
          content="Get in touch with the Radius team. Whether you're a buyer looking for vendors or a vendor wanting to join our network, we're here to help."
        />
        <meta name="keywords" content="contact radius, B2B procurement support, vendor partnership, sales inquiry" />
        <link rel="canonical" href="https://useradius.io/contact" />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <PublicHeader />

        <main className="flex-1">
          {/* Hero Section */}
          <section className="relative overflow-hidden py-12 md:py-20 bg-gradient-to-b from-primary/5 via-background to-background">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.1),transparent_50%)]" />
            <div className="container relative">
              <div className="max-w-3xl mx-auto text-center">
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-6">
                  Get in <span className="text-primary">Touch</span>
                </h1>
                <p className="text-lg text-muted-foreground">
                  Have questions about Radius? Whether you're a buyer exploring our platform or a vendor
                  interested in joining our network, we'd love to hear from you.
                </p>
              </div>
            </div>
          </section>

          {/* Contact Form Section */}
          <section className="py-16">
            <div className="container">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
                {/* Contact Form */}
                <div className="lg:col-span-2">
                  <div className="bg-card border rounded-xl p-8">
                    <h2 className="text-2xl font-semibold mb-6">Send us a message</h2>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name">
                            Name <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="name"
                            placeholder="Your name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                            maxLength={100}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">
                            Email <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="email"
                            type="email"
                            placeholder="you@company.com"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                            maxLength={255}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="company">Company</Label>
                          <Input
                            id="company"
                            placeholder="Your company (optional)"
                            value={formData.company}
                            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                            maxLength={100}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="subject">
                            Subject <span className="text-destructive">*</span>
                          </Label>
                          <Select
                            value={formData.subject}
                            onValueChange={(value) => setFormData({ ...formData, subject: value })}
                          >
                            <SelectTrigger id="subject">
                              <SelectValue placeholder="Select a subject" />
                            </SelectTrigger>
                            <SelectContent>
                              {subjectOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">
                          Message <span className="text-destructive">*</span>
                        </Label>
                        <Textarea
                          id="message"
                          placeholder="How can we help you?"
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          required
                          rows={6}
                          maxLength={2000}
                        />
                        <p className="text-xs text-muted-foreground text-right">
                          {formData.message.length}/2000
                        </p>
                      </div>

                      {/* Turnstile Widget */}
                      <div className="space-y-2">
                        <div ref={turnstileRef} className="flex justify-start"></div>
                        {!turnstileLoaded && (
                          <div className="h-[65px] w-[300px] bg-muted rounded flex items-center justify-center">
                            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                          </div>
                        )}
                      </div>

                      <Button
                        type="submit"
                        size="lg"
                        className="w-full sm:w-auto"
                        disabled={isSubmitting || !turnstileToken}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="mr-2 h-4 w-4" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </form>
                  </div>
                </div>

                {/* Contact Info Sidebar */}
                <div className="lg:col-span-1">
                  <div className="bg-card border rounded-xl p-8 space-y-8 sticky top-24">
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Mail className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">Email</p>
                            <a
                              href="mailto:hello@useradius.io"
                              className="text-sm text-muted-foreground hover:text-primary transition-colors"
                            >
                              hello@useradius.io
                            </a>
                          </div>
                        </div>

                      </div>
                    </div>

                    {/* Live Chat Section */}
                    {!featuresLoading && showLiveChat && (
                      <div className="border-t pt-6">
                        <h3 className="text-lg font-semibold mb-3">Live Chat</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Get instant answers to your questions from our team.
                        </p>
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => {
                            const tryOpenChat = (attempts = 0) => {
                              // Debug: log widget state
                              console.debug('[Chat] Attempt', attempts, {
                                FreshworksWidget: typeof window.FreshworksWidget,
                                hasQueue: !!(window.FreshworksWidget as any)?.q,
                                fcWidget: typeof window.fcWidget,
                                fcWidgetHasOpen: !!window.fcWidget?.open,
                              });

                              // Prefer fcWidget if available (Freshchat SDK)
                              if (window.fcWidget?.open) {
                                console.debug('[Chat] Opening via fcWidget');
                                window.fcWidget.open();
                                return;
                              }

                              // FreshworksWidget - check if it's the real widget (not just the stub with .q queue)
                              const fw = window.FreshworksWidget as any;
                              if (typeof fw === 'function' && !fw.q) {
                                console.debug('[Chat] Opening via FreshworksWidget (ready)');
                                fw('open');
                                return;
                              }

                              // Widget still loading - retry up to 10 times (3 seconds total)
                              if (attempts < 10) {
                                setTimeout(() => tryOpenChat(attempts + 1), 300);
                              } else {
                                console.debug('[Chat] Widget not available after retries');
                                toast({
                                  title: "Chat Unavailable",
                                  description: "Please try again in a moment or email us at hello@useradius.io",
                                  variant: "destructive",
                                });
                              }
                            };
                            tryOpenChat();
                          }}
                        >
                          <MessageCircle className="h-4 w-4 mr-2" />
                          Start a Chat
                        </Button>
                      </div>
                    )}

                    {/* Calendar Booking Section */}
                    {!featuresLoading && showCalendarBooking && (
                      <div className="border-t pt-6">
                        <h3 className="text-lg font-semibold mb-3">Prefer a live conversation?</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Schedule a 30-minute demo to see how Radius can streamline your procurement process.
                        </p>
                        <a
                          href="https://calendly.com/hello-useradius/product-demo"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors w-full"
                        >
                          <Calendar className="h-4 w-4" />
                          Schedule a Demo
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <PublicFooter />
      </div>
    </>
  );
};

export default Contact;
